import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../Common/Modal';
import Alert from '../Common/Alert';
import ElectronicSign from '../Common/ElectronicSign';
import api from '../../services/api';
import visitService from '../../services/visitService';
import { VISIT_TYPES, formatVisitId } from '../../utils/constants';
import { visits as visitMessages, common as commonMessages } from '../../utils/validationMessages';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import letterTemplateService from '../../services/letterTemplateService';
import { buildLetterPartsFromTemplate } from '../../utils/letterTemplateBuilder';
import { downloadVisitLetterPdfFromHtml, getVisitLetterPdfErrorMessage } from '../../utils/visitLetterPdf';
import { extractReportCreatedByBlock } from '../../utils/visitLetterDisplay';
import { formatActionItemStatusForDisplay } from '../../utils/actionItemUi';
import { AuthContext } from '../../context/AuthContext';
import VisitModalHeader from './VisitModalHeader';
import './GenerateVisitLetterModal.css';

const getBodyPlainText = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
};

/** Same markers as follow-up letter: strip before reload / append on save so the Issues block is not duplicated. */
const VISIT_LETTER_ISSUES_MARKER_START = '<!--CTMS_OPEN_ISSUES_TABLE_START-->';
const VISIT_LETTER_ISSUES_MARKER_END = '<!--CTMS_OPEN_ISSUES_TABLE_END-->';

const stripVisitLetterIssuesBlock = (html) => {
  if (!html) return '';
  const startIdx = html.indexOf(VISIT_LETTER_ISSUES_MARKER_START);
  if (startIdx === -1) return html;
  const endIdx = html.indexOf(VISIT_LETTER_ISSUES_MARKER_END, startIdx);
  if (endIdx === -1) return html.slice(0, startIdx).trimEnd();
  return (html.slice(0, startIdx) + html.slice(endIdx + VISIT_LETTER_ISSUES_MARKER_END.length)).trimEnd();
};

const escapeHtml = (value) => {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const REPORT_CREATED_MARKER_START = '<!--CTMS_REPORT_CREATED_BY_START-->';
const REPORT_CREATED_MARKER_END = '<!--CTMS_REPORT_CREATED_BY_END-->';

const stripReportCreatedByBlock = (html) => {
  if (!html) return '';
  const startIdx = html.indexOf(REPORT_CREATED_MARKER_START);
  if (startIdx === -1) return html;
  const endIdx = html.indexOf(REPORT_CREATED_MARKER_END, startIdx);
  if (endIdx === -1) return html.slice(0, startIdx).trimEnd();
  return (html.slice(0, startIdx) + html.slice(endIdx + REPORT_CREATED_MARKER_END.length)).trimEnd();
};

const getSignerFullName = (user) => {
  if (!user || typeof user !== 'object') return '';
  const fn = (user.firstName ?? user.FirstName ?? user.vFirstName ?? '').toString().trim();
  const ln = (user.lastName ?? user.LastName ?? user.vLastName ?? '').toString().trim();
  return [fn, ln].filter(Boolean).join(' ').trim();
};

const getSignerRole = (user) => {
  if (!user || typeof user !== 'object') return '';
  return (user.profileCode ?? user.ProfileCode ?? user.vUserTypeCode ?? user.role ?? user.Role ?? '').toString().trim();
};

const formatDateTimeWithSeconds = () => {
  const d = new Date();
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTHS[d.getMonth()].toUpperCase();
  const year = d.getFullYear();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return { datePart: `${day}-${month}-${year}`, timePart: `${h}:${m}` };
};

const parseVRecordedSign = (signedBy) => {
  if (!signedBy || typeof signedBy !== 'string') return { name: '', role: '' };
  const s = signedBy.trim();
  if (!s) return { name: '', role: '' };
  const firstParen = s.indexOf('(');
  const name = firstParen > 0 ? s.substring(0, firstParen).trim() : s;
  const matches = s.match(/\(([^)]*)\)/g);
  const role = matches && matches.length > 0
    ? matches[matches.length - 1].replace(/[()]/g, '').trim()
    : '';
  return { name, role };
};

const formatUtcWithOffsetToDateTimeParts = (utcDate, offsetStr) => {
  if (utcDate == null) return null;
  const d = new Date(utcDate);
  if (Number.isNaN(d.getTime())) return null;
  let offsetMinutes = 0;
  if (offsetStr) {
    const m = String(offsetStr).trim().match(/^([+-]?)(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (m) {
      offsetMinutes = (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) * (m[1] === '-' ? -1 : 1);
    }
  }
  const local = new Date(d.getTime() + offsetMinutes * 60 * 1000);
  const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const day = String(local.getUTCDate()).padStart(2, '0');
  const month = MONTHS[local.getUTCMonth()];
  const year = local.getUTCFullYear();
  const h = String(local.getUTCHours()).padStart(2, '0');
  const mi = String(local.getUTCMinutes()).padStart(2, '0');
  return { datePart: `${day}-${month}-${year}`, timePart: `${h}:${mi}` };
};

const buildReportCreatedByHtml = (user) => {
  const name = getSignerFullName(user) || '\u00A0';
  const role = getSignerRole(user) || '\u00A0';
  const { datePart, timePart } = formatDateTimeWithSeconds();
  const cellStyle = 'border:1px solid #999;padding:7px 10px;text-align:left;vertical-align:middle;font-size:12px;';
  const labelStyle = `${cellStyle}font-weight:600;color:#134252;width:12%;white-space:nowrap;`;
  const headStyle = `${cellStyle}background-color:#eef2f6;color:#134252;font-weight:700;text-align:left;`;
  return `${REPORT_CREATED_MARKER_START}` +
    `<div style="margin-top:24px;border-top:1px solid #d1d5db;padding-top:14px;">` +
      `<table style="width:100%;border-collapse:collapse;table-layout:auto;font-size:12px;margin-bottom:16px;">` +
        `<thead><tr><th colspan="4" style="${headStyle}">Letter Generated By</th></tr></thead>` +
        `<tbody>` +
          `<tr>` +
            `<td style="${labelStyle}">Signature</td>` +
            `<td style="${cellStyle}">${escapeHtml(name)}</td>` +
            `<td style="${labelStyle}">Role in the Study</td>` +
            `<td style="${cellStyle}">${escapeHtml(role)}</td>` +
          `</tr>` +
          `<tr>` +
            `<td style="${labelStyle}">Name</td>` +
            `<td style="${cellStyle}">${escapeHtml(name)}</td>` +
            `<td style="${labelStyle}">Date</td>` +
            `<td style="${cellStyle}">${escapeHtml(datePart)} ${escapeHtml(timePart)}</td>` +
          `</tr>` +
        `</tbody>` +
      `</table>` +
      `<p style="font-size:11px;color:#555;margin-top:0;margin-bottom:16px;padding:0 2px;">` +
        `<strong>Note:</strong> This is electronically signed document and manual signature is not required.` +
      `</p>` +
    `</div>` +
    `${REPORT_CREATED_MARKER_END}`;
};

const isSsvVisitType = (visit) =>
  String(visit?.visitType ?? visit?.VisitType ?? '').trim().toUpperCase() === 'SSV';

/** e.g. SSV01 → 1, SSV02 → 2 */
const parseSsvDisplaySequence = (visit) => {
  const code = String(visit?.displayVisitCode ?? visit?.DisplayVisitCode ?? '').trim().toUpperCase();
  const m = code.match(/^SSV(\d+)$/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
};

const getVisitRowId = (v) =>
  Number(v?.scheduledVisitID ?? v?.scheduledVisitId ?? v?.ScheduledVisitID ?? 0);

const getIssueScheduledVisitId = (item) =>
  Number(item?.scheduledVisitID ?? item?.ScheduledVisitID ?? item?.scheduledVisitId ?? 0);

/** Matches DB `VStatus` for appendix rows (not the display label from formatActionItemStatusForDisplay). */
const isActionItemDbClosed = (status) =>
  String(status ?? '').trim().toLowerCase() === 'closed';

const getActionItemDedupeKey = (item) => {
  const id = item?.actionItemID ?? item?.nCRFActionItemNo;
  if (id != null && String(id).trim() !== '') return `id:${String(id).trim()}`;
  const ref = item?.actionItemNumber ?? item?.vActionItemNumber;
  if (ref != null && String(ref).trim() !== '') return `ref:${String(ref).trim()}`;
  return `row:${getIssueScheduledVisitId(item)}:${String(item?.actionItemOn ?? '').trim()}:${String(item?.findingDescription ?? '').slice(0, 40)}`;
};

/** Same protocol as the letter context: prefer StudyID (includes SSV/SIV/SMV when ProjectCode is missing or differs by case). */
const visitMatchesLetterPickerScope = (letterVisit, apiVisit) => {
  const letterStudy = Number(letterVisit?.studyID ?? letterVisit?.StudyID ?? 0);
  const vStudy = Number(apiVisit?.studyID ?? apiVisit?.StudyID ?? 0);
  if (letterStudy > 0 && vStudy > 0 && letterStudy === vStudy) return true;
  const letterPc = String(letterVisit?.projectCode ?? letterVisit?.ProjectCode ?? '').trim().toLowerCase();
  const vPc = String(apiVisit?.projectCode ?? apiVisit?.ProjectCode ?? '').trim().toLowerCase();
  return letterPc !== '' && letterPc === vPc;
};

/** All scheduled visits for the site+protocol (picker), including current visit row if missing from API list. */
const buildSiteVisitsPickerList = (sameProjectVisits, currentVisit) => {
  const byId = new Map();
  (sameProjectVisits || []).forEach((v) => {
    const id = getVisitRowId(v);
    if (id > 0) byId.set(id, v);
  });
  const curId = getVisitRowId(currentVisit);
  if (curId > 0 && !byId.has(curId)) {
    byId.set(curId, currentVisit);
  }
  return Array.from(byId.values()).sort((a, b) => {
    const seqA = parseSsvDisplaySequence(a);
    const seqB = parseSsvDisplaySequence(b);
    if (seqA != null && seqB != null && seqA !== seqB) return seqA - seqB;
    if (seqA != null && seqB == null) return -1;
    if (seqA == null && seqB != null) return 1;
    const da = new Date(a.plannedStartDate ?? a.PlannedStartDate ?? 0).getTime();
    const db = new Date(b.plannedStartDate ?? b.PlannedStartDate ?? 0).getTime();
    if (da !== db) return da - db;
    return getVisitRowId(a) - getVisitRowId(b);
  });
};

const visitPickerLabel = (v) => {
  const type = String(v?.visitType ?? v?.VisitType ?? '').trim();
  const typeLabel = VISIT_TYPES[type] || type || 'Visit';
  const idLabel = formatVisitId(v);
  return `${idLabel} (${typeLabel})`;
};

const buildVisitsSummaryLine = (visitsList, selectedIds) => {
  if (!selectedIds?.length || !visitsList?.length) return '';
  const idSet = new Set(selectedIds.map(Number));
  const labels = visitsList
    .filter((v) => idSet.has(getVisitRowId(v)))
    .map((v) => visitPickerLabel(v))
    .filter(Boolean);
  return labels.join(', ');
};

const buildSsvVisitLetterIssuesTableHtml = (issues, visit, visitsSummaryLine) => {
  const rows = Array.isArray(issues) ? issues : [];
  const fallbackSite = visit?.siteCode ?? visit?.SiteCode ?? '';
  const cellStyle = 'border:1px solid #d1d5db;padding:7px 8px;text-align:left;vertical-align:top;font-size:12px;color:#1f2937;word-break:break-word;';
  const headStyle = `${cellStyle}background-color:#eef2f6;color:#134252;font-weight:600;`;
  // Match Generate Preview table columns (8 cols — no Visit Type / Assigned To).
  const headers = ['Reference No', 'Site', 'Visit Date', 'Field Name', 'Issue Description', 'Expected Closure Date', 'Priority', 'Current Status'];
  const headerHtml = headers.map((h) => `<th style="${headStyle}">${escapeHtml(h)}</th>`).join('');
  const scopeLine = (visitsSummaryLine && String(visitsSummaryLine).trim())
    ? `<p style="margin:0 0 12px;font-size:13px;color:#374151;line-height:1.45;"><strong>Selected visits:</strong> ${escapeHtml(String(visitsSummaryLine).trim())}</p>`
    : '';
  const emptyIssuesRowStyle = `${cellStyle}text-align:center;color:#6b7280;font-style:italic;`;
  const bodyHtml =
    rows.length === 0
      ? `<tr><td colspan="8" style="${emptyIssuesRowStyle}">${escapeHtml('Issues are not found')}</td></tr>`
      : rows
          .map((item) => {
            const siteCell = [item.siteCode, item.siteDescription].filter(Boolean).join(' \u2013 ') || fallbackSite || '-';
            const vd = item.visitDate ?? item.VisitDate ?? item.plannedStartDate;
            return `<tr>` +
              `<td style="${cellStyle}">${escapeHtml(item.actionItemNumber ?? item.vActionItemNumber ?? '-')}</td>` +
              `<td style="${cellStyle}">${escapeHtml(siteCell)}</td>` +
              `<td style="${cellStyle}">${escapeHtml(vd ? formatDateDDMMMYYYY(vd) : '-')}</td>` +
              `<td style="${cellStyle}">${escapeHtml(item.actionItemOn || '-')}</td>` +
              `<td style="${cellStyle}">${escapeHtml(item.findingDescription || '-')}</td>` +
              `<td style="${cellStyle}">${escapeHtml(item.dueDate ? formatDateDDMMMYYYY(item.dueDate) : '-')}</td>` +
              `<td style="${cellStyle}">${escapeHtml(item.priorityDescription ?? item.severity ?? '-')}</td>` +
              `<td style="${cellStyle}">${escapeHtml(formatActionItemStatusForDisplay(item.status))}</td>` +
              `</tr>`;
          })
          .join('');
  return `${VISIT_LETTER_ISSUES_MARKER_START}` +
    `<div style="margin-top:18px;margin-bottom:24px;border-top:1px solid #d1d5db;padding-top:14px;padding-bottom:8px;">` +
      `${scopeLine}` +
      `<h3 style="margin:0 0 10px;font-size:16px;font-weight:600;color:#134252;">Issues</h3>` +
      `<table style="width:100%;border-collapse:collapse;table-layout:auto;font-size:12px;">` +
        `<thead><tr>${headerHtml}</tr></thead>` +
        `<tbody>${bodyHtml}</tbody>` +
      `</table>` +
    `</div>` +
    `${VISIT_LETTER_ISSUES_MARKER_END}`;
};

const GenerateVisitLetterModal = ({ isOpen, onClose, visit, resolvedPiName, onGenerated }) => {
  const { user: authUser } = useContext(AuthContext);
  const [editorContent, setEditorContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [letterBodyError, setLetterBodyError] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const bodyEditorRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [blockFormat, setBlockFormat] = useState('p');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [showPreview, setShowPreview] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [toast, setToast] = useState(null);
  const previewContentRef = useRef(null);
  const bodyContentInitializedRef = useRef(false);
  const eSignRemarksRef = useRef('');
  /** Action items for site+project from appendix (client-filtered by selected visits). */
  const [ssvAppendixIssuesRaw, setSsvAppendixIssuesRaw] = useState([]);
  const [siteVisitsPickerList, setSiteVisitsPickerList] = useState([]);
  const [selectedIssueVisitIds, setSelectedIssueVisitIds] = useState([]);
  const [letterAppendixLoading, setLetterAppendixLoading] = useState(false);
  const [storedReportCreatedByHtml, setStoredReportCreatedByHtml] = useState('');
  const [backendCreatorData, setBackendCreatorData] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setHeaderText('');
      setEditorContent('');
      setFooterText('');
      setSaving(false);
      setShowPreview(false);
      setShowESignModal(false);
      setPdfDownloading(false);
      setHistory([]);
      setHistoryIndex(-1);
      setBlockFormat('p');
      setFontFamily('Arial');
      setLetterBodyError('');
      setSsvAppendixIssuesRaw([]);
      setSiteVisitsPickerList([]);
      setSelectedIssueVisitIds([]);
      setLetterAppendixLoading(false);
      setStoredReportCreatedByHtml('');
      setBackendCreatorData(null);
      bodyContentInitializedRef.current = false;
      eSignRemarksRef.current = '';
      return;
    }
    if (!visit?.scheduledVisitID && !visit?.scheduledVisitId) return;

    const visitId = visit.scheduledVisitID ?? visit.scheduledVisitId;
    let cancelled = false;

    const loadProjectTemplate = async (detail) => {
      const projectCode = (detail?.projectCode ?? detail?.ProjectCode ?? visit?.projectCode ?? visit?.ProjectCode ?? '').toString().trim();
      if (!projectCode) {
        setLetterBodyError(visitMessages.visitLetterTemplateNotConfigured);
        setHeaderText('');
        setEditorContent('');
        setFooterText('');
        setHistory([]);
        setHistoryIndex(-1);
        return;
      }
      try {
        const template = await letterTemplateService.getTemplate(projectCode, 'visit');
        const parts = buildLetterPartsFromTemplate(template, detail, authUser);
        setLetterBodyError('');
        setHeaderText(parts.headerText);
        setEditorContent(parts.bodyHtml);
        setFooterText(parts.footerText);
        setHistory([parts.bodyHtml]);
        setHistoryIndex(0);
      } catch (err) {
        const msg = err?.response?.data?.error ?? visitMessages.visitLetterTemplateNotConfigured;
        setLetterBodyError(msg);
        setHeaderText('');
        setEditorContent('');
        setFooterText('');
        setHistory([]);
        setHistoryIndex(-1);
      }
    };

    visitService.getById(visitId).then(async (detail) => {
      if (cancelled) return;
      const rawSaved = (detail.letterBody != null && String(detail.letterBody).trim() !== '') ? detail.letterBody : null;
      const rawFooter = detail.letterFooter ?? '';
      const savedBody = rawSaved ? stripReportCreatedByBlock(stripVisitLetterIssuesBlock(rawSaved)) : null;
      if (rawSaved || rawFooter) {
        setStoredReportCreatedByHtml(
          extractReportCreatedByBlock(rawSaved || '') || extractReportCreatedByBlock(rawFooter)
        );
      }
      if (detail.letterCreatedBySign) {
        setBackendCreatorData({
          sign: detail.letterCreatedBySign,
          utc: detail.letterCreatedOnUtc,
          offset: detail.letterCreatedAtOffset,
        });
      }
      if (savedBody) {
        setLetterBodyError('');
        setHeaderText(detail.letterHeader ?? '');
        setEditorContent(savedBody);
        setFooterText(stripReportCreatedByBlock(rawFooter));
        setHistory([savedBody]);
        setHistoryIndex(0);
      } else {
        await loadProjectTemplate(detail);
      }
      if (!cancelled) {
        setBlockFormat('p');
        setFontFamily('Arial');
        bodyContentInitializedRef.current = false;
      }
    }).catch((err) => {
      if (!cancelled) {
        console.error('Failed to load visit for letter', err);
        setLetterBodyError(err?.response?.data?.error ?? 'Failed to load visit for letter.');
        setHeaderText('');
        setEditorContent('');
        setFooterText('');
        setHistory([]);
        setHistoryIndex(-1);
        setBlockFormat('p');
        setFontFamily('Arial');
        bodyContentInitializedRef.current = false;
      }
    });

    return () => { cancelled = true; };
  }, [isOpen, visit, authUser]);

  useEffect(() => {
    if (!isOpen || !visit) {
      return undefined;
    }
    const siteIdRaw = visit.siteID ?? visit.SiteID ?? visit.siteId;
    const siteId = siteIdRaw != null ? Number(siteIdRaw) : null;
    const projectCode = (visit.projectCode ?? visit.ProjectCode ?? '').toString().trim();
    if (!siteId || siteId <= 0 || !projectCode) {
      setSsvAppendixIssuesRaw([]);
      setSiteVisitsPickerList([]);
      setSelectedIssueVisitIds([]);
      setLetterAppendixLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLetterAppendixLoading(true);
    (async () => {
      try {
        const [appendixRes, siteVisits] = await Promise.all([
          api.get('/crf/visit-report-site-appendix', {
            params: { siteId, projectCode },
          }),
          visitService.getSiteVisits(siteId),
        ]);
        if (cancelled) return;
        const all = Array.isArray(appendixRes?.data?.openIssues) ? appendixRes.data.openIssues : [];
        const sameProjectVisits = (siteVisits || []).filter((v) => visitMatchesLetterPickerScope(visit, v));
        setSsvAppendixIssuesRaw(all);
        const pickerList = buildSiteVisitsPickerList(sameProjectVisits, visit);
        setSiteVisitsPickerList(pickerList);
        const currentVisitId = getVisitRowId(visit);
        if (isSsvVisitType(visit)) {
          setSelectedIssueVisitIds([]);
        } else {
          // Non-SSV: include current visit issues by default (no multi-visit scope).
          setSelectedIssueVisitIds(currentVisitId > 0 ? [currentVisitId] : []);
        }
      } catch (_err) {
        if (!cancelled) {
          setSsvAppendixIssuesRaw([]);
          setSiteVisitsPickerList([]);
          setSelectedIssueVisitIds([]);
        }
      } finally {
        if (!cancelled) setLetterAppendixLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, visit]);

  /** Close visit picker (details) on outside click; native details does not auto-close. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const closeVisitPickersIfOutside = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('.generate-letter-visit-picker')) return;
      document.querySelectorAll('details.generate-letter-visit-picker[open]').forEach((node) => {
        node.removeAttribute('open');
      });
    };
    document.addEventListener('mousedown', closeVisitPickersIfOutside);
    return () => document.removeEventListener('mousedown', closeVisitPickersIfOutside);
  }, [isOpen]);

  // Sync state -> DOM only when opening or resetting letter body, so typing doesn't reset cursor
  useEffect(() => {
    if (isOpen && visit && bodyEditorRef.current && !bodyContentInitializedRef.current) {
      bodyEditorRef.current.innerHTML = editorContent;
      if (editorContent) bodyContentInitializedRef.current = true;
    }
  }, [isOpen, visit, editorContent]);

  const filteredLetterIssues = useMemo(() => {
    const raw = ssvAppendixIssuesRaw || [];
    const siteOpenResolved = raw.filter((item) => !isActionItemDbClosed(item.status));
    if (!selectedIssueVisitIds?.length) {
      return siteOpenResolved;
    }
    const sel = new Set(selectedIssueVisitIds.map(Number));
    const closedForSelected = raw.filter(
      (item) =>
        isActionItemDbClosed(item.status) && sel.has(getIssueScheduledVisitId(item))
    );
    const seen = new Set(siteOpenResolved.map(getActionItemDedupeKey));
    const merged = [...siteOpenResolved];
    closedForSelected.forEach((item) => {
      const key = getActionItemDedupeKey(item);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(item);
      }
    });
    return merged;
  }, [ssvAppendixIssuesRaw, selectedIssueVisitIds]);

  const selectedVisitsSummary = useMemo(
    () => buildVisitsSummaryLine(siteVisitsPickerList, selectedIssueVisitIds),
    [siteVisitsPickerList, selectedIssueVisitIds]
  );

  const allPickerVisitIds = useMemo(
    () =>
      siteVisitsPickerList
        .map(getVisitRowId)
        .filter((id) => id > 0)
        .sort((a, b) => a - b),
    [siteVisitsPickerList]
  );

  const allIssueVisitsSelected =
    allPickerVisitIds.length > 0 &&
    allPickerVisitIds.every((id) => selectedIssueVisitIds.includes(id));

  /** Closed-state label for the visit multi-select (matches common dropdown UX). */
  const visitPickerTriggerLabel = useMemo(() => {
    if (allPickerVisitIds.length === 0) return 'Select visits…';
    const n = selectedIssueVisitIds.length;
    const total = allPickerVisitIds.length;
    if (n === 0) return 'No visits selected';
    if (allIssueVisitsSelected) return 'All visits selected';
    if (n === 1) {
      const row = siteVisitsPickerList.find((v) => getVisitRowId(v) === selectedIssueVisitIds[0]);
      return row ? visitPickerLabel(row) : '1 visit selected';
    }
    return `${n} of ${total} visits selected`;
  }, [
    allPickerVisitIds,
    allIssueVisitsSelected,
    selectedIssueVisitIds,
    siteVisitsPickerList,
  ]);

  /** Callback ref: runs per checkbox instance (main + preview modals each mount one). */
  const syncSelectAllIndeterminate = useCallback(
    (el) => {
      if (!el) return;
      if (allPickerVisitIds.length === 0) {
        el.indeterminate = false;
        return;
      }
      const selected = new Set(selectedIssueVisitIds);
      const some = allPickerVisitIds.some((id) => selected.has(id));
      const all = allPickerVisitIds.every((id) => selected.has(id));
      el.indeterminate = some && !all;
    },
    [allPickerVisitIds, selectedIssueVisitIds]
  );

  const handleToggleSelectAllVisits = () => {
    if (allPickerVisitIds.length === 0) return;
    if (allIssueVisitsSelected) {
      setSelectedIssueVisitIds([]);
    } else {
      setSelectedIssueVisitIds([...allPickerVisitIds]);
    }
  };

  const toggleIssueVisitId = (visitId) => {
    const n = Number(visitId);
    if (!Number.isFinite(n) || n <= 0) return;
    setSelectedIssueVisitIds((prev) => {
      if (prev.includes(n)) {
        return prev.filter((x) => x !== n);
      }
      return [...prev, n].sort((a, b) => a - b);
    });
  };

  if (!visit) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Generate Visit Letter">
        <p>No visit selected.</p>
      </Modal>
    );
  }

  const doSendLetter = async (vRemarks, options = {}) => {
    const { closeAfter = true, isDraft = false } = options;
    let cleanedBody = stripVisitLetterIssuesBlock(editorContent || '');
    cleanedBody = stripReportCreatedByBlock(cleanedBody);
    const summaryForMail = buildVisitsSummaryLine(siteVisitsPickerList, selectedIssueVisitIds);
    const issuesHtml =
      siteVisitsPickerList.length > 0
        ? buildSsvVisitLetterIssuesTableHtml(filteredLetterIssues, visit, summaryForMail)
        : '';
    const reportCreatedByHtml = buildReportCreatedByHtml(authUser);
    // Match Preview order: body → issues → footer → Letter Generated By
    const letterBodyForSend = `${cleanedBody}${issuesHtml}`;
    const cleanedFooter = stripReportCreatedByBlock(footerText || '');
    const letterFooterForSend = `${cleanedFooter}${reportCreatedByHtml}`;
    await visitService.sendConfirmationLetter(visit.scheduledVisitID, {
      letterHeader: headerText,
      letterBody: letterBodyForSend,
      letterFooter: letterFooterForSend,
      piEmail: null,
      vRemarks: vRemarks || undefined,
      isDraft,
    });
    if (onGenerated) onGenerated();
    setToast({ type: 'success', message: commonMessages.authSuccess });
    if (closeAfter) onClose();
  };

  const validateLetterBody = () => {
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.letterBodyEmpty);
      return false;
    }
    if (!text) {
      setLetterBodyError(visitMessages.letterBodyShort);
      return false;
    }
    setLetterBodyError('');
    return true;
  };

  const handlePreview = () => {
    setLetterBodyError('');
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.letterPreviewEmpty);
      return;
    }
    if (!text) {
      setLetterBodyError(visitMessages.letterBodyShort);
      return;
    }
    setLetterBodyError('');
    setShowPreview(true);
  };

  const handlePreviewDownloadPdf = async () => {
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setToast({ type: 'error', message: visitMessages.letterPreviewEmpty });
      return;
    }
    const html = previewContentRef.current?.outerHTML ?? '';
    if (!html.trim()) {
      setToast({ type: 'error', message: visitMessages.letterPreviewEmpty });
      return;
    }
    const visitCode = formatVisitId(visit);
    setPdfDownloading(true);
    try {
      await downloadVisitLetterPdfFromHtml({
        html,
        fileName: `Visit-Letter-${visitCode}.pdf`,
        title: 'Visit Letter',
      });
      setToast({ type: 'success', message: visitMessages.letterDownloadedSuccess });
    } catch (err) {
      const message = await getVisitLetterPdfErrorMessage(err);
      setToast({ type: 'error', message: message || err?.message || 'PDF download failed' });
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!visit.scheduledVisitID) return;
    setLetterBodyError('');
    if (!validateLetterBody()) return;
    eSignRemarksRef.current = '';
    setShowESignModal(true);
  };

  const runAfterESign = () => {
    const vRemarks = eSignRemarksRef.current || '';
    setShowESignModal(false);
    eSignRemarksRef.current = '';
    setSaving(true);
    doSendLetter(vRemarks, { closeAfter: true, isDraft: false })
      .catch((err) => {
        console.error('Error generating visit letter:', err);
        const msg = err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Unable to create or update visit letter. Please try again.';
        setToast({ type: 'error', message: msg });
      })
      .finally(() => setSaving(false));
  };

  const handleSaveAndContinue = (e) => {
    e.preventDefault();
    if (!visit.scheduledVisitID) return;
    setLetterBodyError('');
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.noDataToSave);
      return;
    }
    setSaving(true);
    doSendLetter('', { isDraft: true, closeAfter: true })
      .catch((err) => {
        console.error('Error saving visit letter draft:', err);
        const msg = err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Unable to save visit letter draft. Please try again.';
        setToast({ type: 'error', message: msg });
      })
      .finally(() => setSaving(false));
  };

  const handleClear = () => {
    setLetterBodyError('');
    bodyContentInitializedRef.current = false;
    setEditorContent('');
    setHistory(['']);
    setHistoryIndex(0);
    setStoredReportCreatedByHtml('');
    if (bodyEditorRef.current) {
      bodyEditorRef.current.innerHTML = '';
    }
  };

  const visitTypeLabel = VISIT_TYPES[visit.visitType] || visit.visitType || '';

  // Helper to push new body content into history (for undo/redo)
  const pushHistory = (content) => {
    setHistory(prev => {
      const trimmed = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      if (trimmed.length > 0 && trimmed[trimmed.length - 1] === content) {
        return trimmed;
      }
      const next = [...trimmed, content];
      setHistoryIndex(next.length - 1);
      return next;
    });
  };

  // Simple rich text commands using document.execCommand
  const applyCommand = (command, value = null) => {
    if (!bodyEditorRef.current) return;
    bodyEditorRef.current.focus();
    document.execCommand(command, false, value);
    // Update state with latest HTML after command
    const html = bodyEditorRef.current.innerHTML;
    setEditorContent(html);
    pushHistory(html);
  };

  const handleBodyInput = (e) => {
    const html = e.currentTarget.innerHTML;
    setEditorContent(html);
    setLetterBodyError('');
    pushHistory(html);
  };

  const handleUndoClick = () => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    const html = history[newIndex];
    setEditorContent(html);
    if (bodyEditorRef.current) {
      bodyEditorRef.current.innerHTML = html;
    }
  };

  const handleRedoClick = () => {
    if (historyIndex === -1 || historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    const html = history[newIndex];
    setEditorContent(html);
    if (bodyEditorRef.current) {
      bodyEditorRef.current.innerHTML = html;
    }
  };

  const renderSsvLetterIssuesTable = (options = {}) => {
    const { hideVisitPicker = false } = options;
    if (letterAppendixLoading) {
      return (
        <div className="follow-up-open-issues-section">
          <p className="text-muted small mb-0">Loading issues for letter…</p>
        </div>
      );
    }
    if (siteVisitsPickerList.length === 0) return null;
    return (
      <div className="follow-up-open-issues-section">
        <div
          className={
            hideVisitPicker
              ? 'generate-letter-visit-filter generate-letter-visit-filter--preview'
              : 'generate-letter-visit-filter'
          }
        >
          {!hideVisitPicker ? (
            <>
              <label className="generate-letter-visit-filter-label">Visits</label>
              <details className="generate-letter-visit-picker">
                <summary className="generate-letter-visit-picker-summary">
                  <span className="generate-letter-visit-picker-summary-text">{visitPickerTriggerLabel}</span>
                  <span className="generate-letter-visit-picker-chevron" aria-hidden />
                </summary>
                <div className="generate-letter-visit-picker-body">
                  {allPickerVisitIds.length > 1 ? (
                    <label className="generate-letter-visit-picker-row generate-letter-visit-picker-select-all">
                      <span className="generate-letter-visit-picker-check-wrap">
                        <input
                          ref={syncSelectAllIndeterminate}
                          type="checkbox"
                          checked={allIssueVisitsSelected}
                          onChange={handleToggleSelectAllVisits}
                        />
                      </span>
                      <span className="generate-letter-visit-picker-row-text">Select All</span>
                    </label>
                  ) : null}
                  {siteVisitsPickerList.map((v) => {
                    const id = getVisitRowId(v);
                    return (
                      <label key={id} className="generate-letter-visit-picker-row">
                        <span className="generate-letter-visit-picker-check-wrap">
                          <input
                            type="checkbox"
                            checked={selectedIssueVisitIds.includes(id)}
                            onChange={() => toggleIssueVisitId(id)}
                          />
                        </span>
                        <span className="generate-letter-visit-picker-row-text">{visitPickerLabel(v)}</span>
                      </label>
                    );
                  })}
                </div>
              </details>
            </>
          ) : null}
          <div className="generate-letter-selected-visits-text">
            <span className="generate-letter-selected-visits-label">Selected visits:</span>{' '}
            <span className="generate-letter-selected-visits-value">{selectedVisitsSummary || '—'}</span>
          </div>
        </div>
        <>
          <h3 className="follow-up-open-issues-title">Issues</h3>
          <div className="table-responsive">
            <table className="follow-up-open-issues-table">
              <thead>
                <tr>
                  <th>Reference No</th>
                  <th>Site</th>
                  <th>Visit Date</th>
                  <th>Field Name</th>
                  <th>Issue Description</th>
                  <th>Expected Closure Date</th>
                  <th>Priority</th>
                  <th>Current Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredLetterIssues.length === 0 ? (
                  <tr className="follow-up-open-issues-not-found-row">
                    <td colSpan={8}>Issues are not found</td>
                  </tr>
                ) : (
                  filteredLetterIssues.map((item) => {
                    const siteCell = [item.siteCode, item.siteDescription].filter(Boolean).join(' – ')
                      || visit.siteCode
                      || visit.SiteCode
                      || '-';
                    const vd = item.visitDate ?? item.VisitDate ?? item.plannedStartDate;
                    return (
                      <tr key={item.actionItemID ?? item.nCRFActionItemNo ?? item.actionItemNumber}>
                        <td>{item.actionItemNumber ?? item.vActionItemNumber ?? '-'}</td>
                        <td>{siteCell}</td>
                        <td>{vd ? formatDateDDMMMYYYY(vd) : '-'}</td>
                        <td>{item.actionItemOn || '-'}</td>
                        <td>{item.findingDescription || '-'}</td>
                        <td>{item.dueDate ? formatDateDDMMMYYYY(item.dueDate) : '-'}</td>
                        <td>{item.priorityDescription ?? item.severity ?? '-'}</td>
                        <td>{formatActionItemStatusForDisplay(item.status)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      </div>
    );
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title="Generate Visit Letter"
      className="generate-letter-modal"
    >
      <form onSubmit={handleSubmit}>
        <VisitModalHeader
          projectCode={visit.projectCode}
          siteName={visit.siteName}
          siteCode={visit.siteCode ?? visit.SiteCode}
          piName={resolvedPiName ?? visit.piName ?? visit.PIName ?? visit.pIName}
        />

        <div className="form-group">
          <label className="form-label">Visit Type</label>
          <input
            type="text"
            className="form-input"
            value={visitTypeLabel}
            disabled
          />
        </div>

        {/* Header part (read-only) */}
        <div className="form-group generate-letter-header-block">
          
          <textarea
            className="form-textarea generate-letter-template"
            rows="6"
            value={headerText}
            readOnly
          />
        </div>

        {/* Rich text toolbar + editor for body */}
        <div className="form-group">
          

          {/* Toolbar inspired by wireframe */}
          <div className="richtext-toolbar">
            <button type="button" className="rt-btn" onClick={() => applyCommand('bold')}>
              B
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('italic')}>
              I
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('underline')}>
              U
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('insertUnorderedList')}>
              •
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('insertOrderedList')}>
              1.
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('justifyLeft')}>
              L
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('justifyCenter')}>
              C
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('justifyRight')}>
              R
            </button>
            <button type="button" className="rt-btn" onClick={handleUndoClick}>
              ↺
            </button>
            <button type="button" className="rt-btn" onClick={handleRedoClick}>
              ↻
            </button>
            <div className="rt-select-wrap">
              <span className="rt-select-label">{blockFormat === 'h1' ? 'Heading 1' : blockFormat === 'h2' ? 'Heading 2' : blockFormat === 'h3' ? 'Heading 3' : 'Normal'}</span>
              <select
                className="rt-select"
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) return;
                  setBlockFormat(value);
                  applyCommand('formatBlock', value);
                }}
                value={blockFormat}
                aria-label="Block format"
              >
                <option value="p">Normal</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
              </select>
            </div>
            <div className="rt-select-wrap rt-select-wrap--font">
              <span className="rt-select-label">{fontFamily || 'Arial'}</span>
              <select
                className="rt-select"
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) return;
                  applyCommand('fontName', value);
                  setFontFamily(value);
                }}
                value={fontFamily}
                aria-label="Font family"
              >
                <option value="">Font Family</option>
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Calibri">Calibri</option>
                <option value="Georgia">Georgia</option>
              </select>
            </div>
          </div>

          <div
            ref={bodyEditorRef}
            className="form-textarea richtext-editor"
            contentEditable
            suppressContentEditableWarning
            onInput={handleBodyInput}
          />
          {letterBodyError && (
            <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{letterBodyError}</div>
          )}
        </div>

        {renderSsvLetterIssuesTable()}

        {/* Footer part (read-only) */}
        <div className="form-group generate-letter-footer-block">
          
          <textarea
            className="form-textarea generate-letter-template"
            rows="4"
            value={footerText}
            readOnly
          />
        </div>

        <div className="generate-letter-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handlePreview}
            disabled={saving}
          >
            Preview
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveAndContinue}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Draft Save'}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Generating...' : 'Submit'}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClear}
            disabled={saving}
          >
            Clear
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </form>
    </Modal>

    {/* Visit Letter Preview Modal */}
    <Modal
      isOpen={showPreview}
      onClose={() => setShowPreview(false)}
      size="large"
      title="Generate Visit Letter Preview"
      className="generate-letter-preview-modal"
      footer={
        <>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handlePreviewDownloadPdf}
            disabled={pdfDownloading || letterAppendixLoading}
          >
            {pdfDownloading ? 'Downloading…' : 'Download'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowPreview(false)}
          >
            Close
          </button>
        </>
      }
    >
      <div ref={previewContentRef} className="generate-letter-preview-content">
        {headerText && (
          <pre className="generate-letter-preview-header">{headerText}</pre>
        )}
        <div
          className="generate-letter-preview-body"
          dangerouslySetInnerHTML={{ __html: editorContent }}
        />
        {renderSsvLetterIssuesTable({ hideVisitPicker: true })}
        {footerText && (
          <pre className="generate-letter-preview-footer">{footerText}</pre>
        )}
        {storedReportCreatedByHtml ? (
          <div dangerouslySetInnerHTML={{ __html: storedReportCreatedByHtml }} />
        ) : (() => {
          let signerName, signerRole, datePart, timePart;
          if (backendCreatorData) {
            const parsed = parseVRecordedSign(backendCreatorData.sign);
            signerName = parsed.name || '\u00A0';
            signerRole = parsed.role || '\u00A0';
            const dtParts = formatUtcWithOffsetToDateTimeParts(backendCreatorData.utc, backendCreatorData.offset);
            datePart = dtParts ? dtParts.datePart : '';
            timePart = dtParts ? dtParts.timePart : '';
          } else {
            signerName = getSignerFullName(authUser) || '\u00A0';
            signerRole = getSignerRole(authUser) || '\u00A0';
            const dt = formatDateTimeWithSeconds();
            datePart = dt.datePart;
            timePart = dt.timePart;
          }
          return (
            <div className="letter-report-created-section">
              <table className="letter-report-created-table">
                <thead>
                  <tr><th colSpan="4" className="letter-report-created-header">Letter Generated By</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="letter-report-created-label">Signature</td>
                    <td>{signerName}</td>
                    <td className="letter-report-created-label">Role in the Study</td>
                    <td>{signerRole}</td>
                  </tr>
                  <tr>
                    <td className="letter-report-created-label">Name</td>
                    <td>{signerName}</td>
                    <td className="letter-report-created-label">Date</td>
                    <td>{datePart} {timePart}</td>
                  </tr>
                </tbody>
              </table>
              <p className="letter-report-created-note">
                <strong>Note:</strong> This is electronically signed document and manual signature is not required.
              </p>
            </div>
          );
        })()}
      </div>
    </Modal>

    <ElectronicSign
      show={showESignModal}
      onClose={() => setShowESignModal(false)}
      onAuthSuccess={(vRemarks) => { eSignRemarksRef.current = vRemarks || ''; }}
      onSave={runAfterESign}
    />

    {toast && (
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
        <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      </div>
    )}
    </>
  );
};

export default GenerateVisitLetterModal;

